import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult
} from '@tanstack/react-query'
import APIService from 'api/APIService'
import { ErrorMessage } from 'types/utility'
import { docCookies } from 'utils/docCookies'

type QueryKey = string[]

// eslint-disable-next-line
type PromiseLikeFn = (...params: any[]) => Promise<any>

// Public shape returned by every hook produced by `createDataHook`.
// Equals v5's `UseQueryResult` plus the three helpers we layer on top.
// Declared explicitly so removed/renamed RQ fields surface at type-check
// time instead of silently at runtime (e.g. v5 dropped `remove` from the
// query result — we now provide our own, and this type makes that contract
// visible to callers).
export type DataHookResult<TData> = UseQueryResult<TData, ErrorMessage> & {
  invalidate: () => Promise<void>
  setData: (newData: TData) => TData | undefined
  remove: () => void
}

type DataHookArgs<FetchFnType extends PromiseLikeFn> = {
  params?: Parameters<FetchFnType>[0]
  opts?: Omit<
    UseQueryOptions<
      Awaited<ReturnType<FetchFnType>>,
      ErrorMessage,
      Awaited<ReturnType<FetchFnType>>,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  > & {
    queryKey?: QueryKey
    onSuccess?: (data: Awaited<ReturnType<FetchFnType>>) => void
    onError?: (err: ErrorMessage) => void
    onSettled?: (
      data: Awaited<ReturnType<FetchFnType>> | undefined,
      err: ErrorMessage | null
    ) => void
  }
}

export type DataHook<FetchFnType extends PromiseLikeFn> = (
  args?: DataHookArgs<FetchFnType>
) => DataHookResult<Awaited<ReturnType<FetchFnType>>>

const createDataHook = <FetchFnType extends PromiseLikeFn>(
  queryKeyBase: QueryKey,
  fetchFn: FetchFnType
): DataHook<FetchFnType> => {
  type TData = Awaited<ReturnType<FetchFnType>>

  return ({
    params,
    opts
  }: DataHookArgs<FetchFnType> = {}): DataHookResult<TData> => {
    const key = opts?.queryKey ?? queryKeyBase
    const resolvedKey = params
      ? key.map((path) =>
          path[0] === ':' ? params[path.replace(':', '')] : path
        )
      : key
    // Stabilize by the concrete value tuple so insertion-order of `params`
    // doesn't matter — only the values that actually land in the queryKey do.
    const queryKeyHash = resolvedKey.join('|')
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const queryKey = useMemo(() => resolvedKey, [queryKeyHash])

    const isAuthenticated =
      !!localStorage.getItem('user-token-id') ||
      !!docCookies.getItem('motus_access_token')
    // This query allows for both authenticated and unauthenticated requests
    const isIndividualReport = queryKeyBase.join('/') === 'reports/:reportId'

    const queryClient = useQueryClient()
    const invalidate = useCallback(
      () => queryClient.invalidateQueries({ queryKey }),
      [queryClient, queryKey]
    )
    // @FIXME: Data is not updating properly even though new data is fresh
    const setData = useCallback(
      (newData: TData) => queryClient.setQueryData<TData>(queryKey, newData),
      [queryClient, queryKey]
    )

    const remove = useCallback(() => {
      queryClient.removeQueries({ queryKey })
    }, [queryClient, queryKey])

    const {
      onSuccess,
      onError,
      onSettled,
      queryKey: _ignoredQueryKey,
      enabled: optsEnabled,
      ...queryOpts
    } = opts ?? {}

    const query = useQuery<TData, ErrorMessage, TData, QueryKey>({
      queryKey,
      queryFn: () => fetchFn(params),
      ...queryOpts,
      // Conditionally disable the query based on authentication status
      // This ensures protected queries don't run without proper authentication
      enabled: isAuthenticated ? (optsEnabled ?? undefined) : isIndividualReport
    })

    const { data, error, dataUpdatedAt, errorUpdatedAt, status } = query

    // v3 compat: fire callbacks once per *fresh* fetch resolution.
    // We snapshot the timestamps at mount and only fire when they advance,
    // so cached data on first render does NOT re-fire onSuccess/onError —
    // matching v3 behavior (which only fired callbacks for fresh fetches,
    // not cache hits).
    const lastDataAt = useRef(dataUpdatedAt)
    const lastErrorAt = useRef(errorUpdatedAt)
    const lastSettledDataAt = useRef(dataUpdatedAt)
    const lastSettledErrorAt = useRef(errorUpdatedAt)

    useEffect(() => {
      if (
        dataUpdatedAt > lastDataAt.current &&
        status === 'success' &&
        data !== undefined
      ) {
        onSuccess?.(data)
      }
      lastDataAt.current = dataUpdatedAt
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataUpdatedAt])

    useEffect(() => {
      if (errorUpdatedAt > lastErrorAt.current && status === 'error' && error) {
        onError?.(error)
      }
      lastErrorAt.current = errorUpdatedAt
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorUpdatedAt])

    useEffect(() => {
      const dataAdvanced = dataUpdatedAt > lastSettledDataAt.current
      const errorAdvanced = errorUpdatedAt > lastSettledErrorAt.current
      if (
        (dataAdvanced || errorAdvanced) &&
        (status === 'success' || status === 'error')
      ) {
        onSettled?.(data, error ?? null)
      }
      lastSettledDataAt.current = dataUpdatedAt
      lastSettledErrorAt.current = errorUpdatedAt
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataUpdatedAt, errorUpdatedAt])

    // v5's `isLoading` matches v3's semantics ("no data AND actively fetching"
    // — false for disabled queries). It's exposed via the `...query` spread.
    // Callers that need v5's "no data yet" semantics should use `isPending`.
    return {
      ...query,
      invalidate,
      setData,
      remove
    }
  }
}

export const useUserProfile = createDataHook(
  ['users', 'me'],
  APIService.users.getCurrentUser
)
export const useReport = createDataHook(
  ['reports', ':reportId'],
  APIService.reports.getReport
)
export const useTeam = createDataHook(
  ['team', ':teamId'],
  APIService.teams.getTeam
)
export const useTags = createDataHook(
  [
    'team',
    ':teamId',
    'tags',
    ':page_size',
    ':q',
    ':page',
    ':sort_by',
    ':sort_direction',
    'archived',
    ':archived'
  ],
  APIService.tags.getTags
)
export const useTeamCategories = createDataHook(
  [
    'team',
    ':teamId',
    'categories',
    ':page_size',
    ':q',
    ':page',
    ':sort_by',
    ':sort_direction',
    'archived',
    ':archived'
  ],
  APIService.tags.getTeamCategories
)

export const useRoles = createDataHook(['roles'], APIService.misc.getRoles)
export const usePrograms = createDataHook(
  ['team', ':teamId', 'programs', ':page', ':page_size'],
  APIService.programs.getPrograms
)
export const usePaymentSchedules = createDataHook(
  ['payment-schedules', ':kind'],
  APIService.schedules.getPaymentSchedules
)
export const usePaymentSchedule = createDataHook(
  ['payment-schedule', ':id'],
  APIService.schedules.getPaymentSchedule
)
export const useProgram = createDataHook(
  ['team', ':teamId', 'program', ':programId'],
  APIService.programs.getProgram
)

export const useProgramRates = createDataHook(
  ['team', ':teamId', 'program', ':programId', 'rates'],
  APIService.programs.getProgramRates
)

export const useProgramPurposes = createDataHook(
  ['team', ':teamId', 'program', ':programId', 'purposes'],
  APIService.programs.getProgramPurposes
)

export const useProgramHistory = createDataHook(
  ['user-program-history', ':userId'],
  APIService.programs.getProgramHistory
)

export const useMembers = createDataHook(
  [
    'members',
    ':teamId',
    'search',
    ':q',
    ':is_manager',
    ':filters',
    ':filterByTeam'
  ],
  APIService.members.getTeamMembers
)

export const useMember = createDataHook(
  ['members', ':teamId', ':memberId'],
  APIService.members.getMember
)

export const usePayPeriod = createDataHook(
  ['pay-periods', ':id'],
  APIService.payPeriods.getPayPeriod
)

export const usePayPeriods = createDataHook(
  ['pay-periods', ':status', ':pageSize', ':page', ':issues'],
  APIService.payPeriods.getPayPeriods
)

export const usePreviewPaySchedule = createDataHook(
  ['preview-pay-schedule', ':filters'],
  APIService.schedules.getPaymentSchedulePreview
)

export const useRuleIntrospection = createDataHook(
  ['rule-introspection'],
  APIService.rules.getRuleIntrospection
)

export const useUserComplianceSubmission = createDataHook(
  ['submission', 'me', ':annualized_mileage_year', ':use_new_endpoint'], // confirm with backend if no_program_enabled is still being used - if not remove it
  APIService.compliance.getUserSubmission
)

export const useUserComplianceHistory = createDataHook(
  ['compliance-history', 'me'],
  APIService.compliance.getUserComplianceHistory
)

export const useComplianceHistory = createDataHook(
  ['compliance-history', ':user_id'],
  APIService.compliance.getComplianceHistory
)
export const useAccountant = createDataHook(
  ['user', 'accountants'],
  APIService.misc.getAccountantGroup
)

export const useSubscriptionInvoices = createDataHook(
  ['subscription', 'invoices'],
  APIService.subscriptions.getSubscriptionInvoices
)
export const useOnboardingStats = createDataHook(
  ['onboarding', 'stats', ':teamId', ':archived', ':include_invites'],
  APIService.members.getOnboardingStats
)

export const getSLT = createDataHook(
  ['slt', ':tokenId', ':authToken'],
  APIService.everlance.getSLT
)

export const useReportPayPeriod = createDataHook(
  ['reports', 'pay-periods'],
  APIService.reports.getReportPayPeriods
)

export const useTotals = createDataHook(
  [
    'trips',
    'totals',
    'me',
    ':category',
    ':date_range',
    ':institution_account_token_id',
    ':limit',
    ':purpose',
    ':income_source',
    ':starting_after',
    ':transaction_type',
    ':token_ids',
    ':uncategorized',
    ':motusPurpose',
    ':motusHasAnomaly',
    ':trip_seq_num_cursor',
    ':trip_day_cursor'
  ],
  APIService.users.getTotals
)

export const useCountries = createDataHook(
  ['catalog', 'countries'],
  APIService.catalog.getCountries
)
